/**
 * OtpQr — the enrolment QR code (A.4, FR-011; research §1.5; sheet 07b
 * callout 4).
 *
 * The API returns the `otpauth://` URI; the browser encodes it with
 * `qrcode-generator` (zero dependencies, bundled types) as an inline SVG. The
 * container is `role="img"` with an `aria-label` that names the manual key as
 * the text alternative, which a canvas could not offer. The secret is already
 * in the response as the manual key, so encoding it client-side leaks nothing
 * further.
 *
 * The only markup that goes through `dangerouslySetInnerHTML` is the library's
 * own output, and only when it starts with `<svg` (tested). Nothing from the
 * URI is interpolated into HTML by this component: the library escapes it into
 * path data.
 */
import { useMemo } from 'react'
import qrcode from 'qrcode-generator'

export const OTP_QR_LABEL =
  'QR code for your authenticator app. If you cannot scan it, enter the manual key shown below.'

/** Error correction M, automatic version, scalable SVG (viewBox, no fixed size). */
export function renderQrSvg(uri: string): string {
  const qr = qrcode(0, 'M')
  qr.addData(uri)
  qr.make()
  return qr.createSvgTag({ scalable: true })
}

export function OtpQr({ uri }: { uri: string }) {
  const svg = useMemo(() => (uri ? renderQrSvg(uri) : ''), [uri])
  if (!svg.startsWith('<svg')) return null
  return (
    <div
      role="img"
      aria-label={OTP_QR_LABEL}
      className="mx-auto w-[200px] rounded-lg border border-navy-200 bg-white p-3 [&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}
